/**
 * HttpClient - Base HTTP communication layer
 * Handles all HTTP requests with proper error handling, authentication, and type safety
 */

import { ApiError, NetworkError } from '../../types/errors';
import type { AuthProvider } from '../auth/AuthProvider';
import { getCsrfToken, requiresCsrfToken } from '../../utils/auth/csrfManager';

/**
 * Request configuration options
 */
export interface RequestConfig extends Omit<RequestInit, 'method' | 'body' | 'headers'> {
  params?: Record<string, string | number | boolean>;
  timeout?: number;
  skipAuth?: boolean;
}

/**
 * HTTP client for making API requests
 * Provides centralized error handling, authentication, and request/response processing
 */
export class HttpClient {
  constructor(
    private readonly baseURL: string,
    private readonly authProvider: AuthProvider
  ) {}

  /**
   * Make a GET request
   */
  public async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'GET',
      ...config,
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Make a POST request
   */
  public async post<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'POST',
      body: this.serializeBody(data),
      ...config,
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Make a PUT request
   */
  public async put<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'PUT',
      body: this.serializeBody(data),
      ...config,
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Make a PATCH request
   */
  public async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'PATCH',
      body: this.serializeBody(data),
      ...config,
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Make a DELETE request
   */
  public async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'DELETE',
      ...config,
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Get a file/blob response (for downloads)
   * Returns the raw Response object to allow blob extraction and header access
   */
  public async getBlob(endpoint: string, config?: RequestConfig): Promise<Response> {
    const url = this.buildURL(endpoint, config?.params);
    const response = await this.makeRequest(url, {
      method: 'GET',
      ...config,
    });

    // Check for error responses
    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return response;
  }

  /**
   * Make the actual fetch request with proper error handling
   *
   * SECURITY: VULN-001 FIX - CSRF Protection
   * Passes HTTP method to buildHeaders for CSRF token injection
   */
  private async makeRequest(
    url: string,
    config: RequestInit & { skipAuth?: boolean; timeout?: number }
  ): Promise<Response> {
    const { skipAuth, timeout, ...fetchConfig } = config;

    try {
      // Build headers with CSRF token support (VULN-001 FIX)
      const headers = this.buildHeaders(fetchConfig.body, skipAuth, fetchConfig.method as string);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = timeout
        ? setTimeout(() => controller.abort(), timeout)
        : undefined;

      // Make request with cookie credentials
      const response = await fetch(url, {
        ...fetchConfig,
        headers,
        credentials: 'include',  // ✅ Send cookies on all requests (cookie-based auth)
        signal: controller.signal,
      });

      // Clear timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      return response;
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError('Network request failed. Please check your connection.', error);
      }

      // Handle timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new NetworkError('Request timeout. Please try again.', error);
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Build complete URL with query parameters
   */
  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const url = `${this.baseURL}${endpoint}`;

    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');

    return `${url}?${queryString}`;
  }

  /**
   * Build request headers
   * Note: Cookie-based auth - tokens are in HttpOnly cookies, not Authorization headers
   *
   * SECURITY: VULN-001 FIX - CSRF Protection
   * Adds X-CSRF-Token header for state-changing requests (POST, PUT, DELETE, PATCH)
   */
  private buildHeaders(body?: BodyInit, skipAuth?: boolean, method?: string): Record<string, string> {
    const headers: Record<string, string> = {};

    // Cookie-based auth: Authorization header not needed (deprecated)
    // Kept for backward compatibility, but authProvider.getToken() returns null
    if (!skipAuth) {
      const token = this.authProvider.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Add Content-Type for JSON data
    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // SECURITY: Add CSRF token for state-changing requests (VULN-001 FIX)
    if (method && requiresCsrfToken(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      } else {
        // Log warning only once per session to avoid spam
        if (!sessionStorage.getItem('csrf_warning_shown')) {
          console.warn(
            '[SECURITY] CSRF token not available. Backend should set csrf_token cookie. ' +
            'State-changing requests are vulnerable to CSRF attacks without this token.'
          );
          sessionStorage.setItem('csrf_warning_shown', 'true');
        }
      }
    }

    return headers;
  }

  /**
   * Serialize request body
   */
  private serializeBody(data: unknown): BodyInit | undefined {
    if (!data) {
      return undefined;
    }

    if (data instanceof FormData) {
      return data;
    }

    if (typeof data === 'string') {
      return data;
    }

    return JSON.stringify(data);
  }

  /**
   * Handle response and parse data
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Success response
    if (response.ok) {
      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      // For responses without content (204 No Content)
      if (response.status === 204) {
        return undefined as T;
      }

      // Try to parse as text
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        return text as T;
      }
    }

    // Error response - parse and throw structured error
    await this.handleErrorResponse(response);

    // This line should never be reached, but TypeScript requires it
    throw new ApiError('Unknown error occurred', response.status);
  }

  /**
   * Handle error responses
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let errorMessage = response.statusText || 'An error occurred';
    let errorCode: string | undefined;
    let errorDetails: unknown;

    // Try to parse error body
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.detail || errorMessage;
        errorCode = errorData.code;
        errorDetails = errorData;
      }
    } catch {
      // If parsing fails, use statusText
    }

    // Create and throw appropriate error
    throw new ApiError(errorMessage, response.status, errorCode, errorDetails);
  }
}
